import { ExpertProfileType } from '@@types/api.types.ts';
import { ExpertProfileComponentProps } from '@components/pages/expert-profile/components/expert-profile-modal.component.tsx';
import { Avatar } from '@components/shared/avatar.component';
import { ErrorMessage } from '@components/shared/error-message.component';
import { FormField } from '@components/shared/form-field.component';
import { FormLabel } from '@components/shared/form-label.component';
import { PopoverSelector } from '@components/shared/popover-selector.component.tsx';
import { Input } from '@components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { useGetServices } from '@queries/service.query';
import { useGetCurrentUser } from '@queries/users.query.ts';
import { countries, usStates } from '@utils/constants';
import { mapToSelectOptions } from '@utils/select.util';
import { FunctionComponent, useMemo } from 'react';

interface ProfileStepProps extends ExpertProfileComponentProps {}

const countryOptions = mapToSelectOptions(countries);
const serviceCountries = countryOptions.filter((country) => ['US', 'GB', 'AU'].includes(country.value));

export const ExpertProfileModalProfileStep: FunctionComponent<ProfileStepProps> = (props) => {
  const { formData, handleChange, errors, setFormData } = props;

  const { data: currentUser } = useGetCurrentUser();
  const { data: servicesData, isLoading: isLoadingServices } = useGetServices();

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => {
      const newState = {
        ...prev,
        [name]: value,
      };

      if (name === 'countryOfService' && value !== prev.countryOfService) {
        newState.serviceId = undefined;
      }
      return newState;
    });
  };

  const serviceOptions = useMemo(() => {
    if (!formData.countryOfService || !servicesData) {
      return [];
    }

    const countryCode = formData.countryOfService;
    const targetCountry = countries.find((country) => country.code === countryCode);
    const targetCountryName = targetCountry?.name;

    const selectedCountryData = servicesData.find((service) => service.name === targetCountryName);

    if (selectedCountryData && selectedCountryData.subServices) {
      return selectedCountryData.subServices.map((subService) => ({
        id: subService.id,
        name: subService.name,
        slug: subService.slug,
      }));
    }

    return [];
  }, [formData.countryOfService, servicesData]);

  const selectedService = useMemo(() => {
    if (!formData.serviceId || !serviceOptions || serviceOptions.length === 0) {
      return null;
    }
    return serviceOptions.find((option) => option.id === formData.serviceId) || null;
  }, [formData.serviceId, serviceOptions]);

  return (
    <div className="flex w-full flex-col items-start gap-5 px-1">
      <div className="flex w-full items-center gap-4">
        <FormField>
          <FormLabel
            label="LinkedIn URL"
            htmlFor="linkedinUrl"
          />
          <Input
            id="linkedinUrl"
            name="linkedinUrl"
            type="url"
            value={formData.linkedinUrl || ''}
            placeholder="https://linkedin.com/in/johndoe"
            onChange={handleChange}
            className="flex h-10 w-full rounded-lg bg-gray-100 p-3"
          />
          <ErrorMessage zodError={errors.linkedinUrl} />
        </FormField>
        <Avatar
          altText={currentUser?.profile?.firstName || currentUser?.email || 'User Avatar'}
          imageUrl={currentUser?.avatarUrl}
          size="lg"
          className="mt-6"
        />
      </div>

      <FormField>
        <FormLabel
          label="Website URL"
          htmlFor="websiteUrl"
        />
        <Input
          id="websiteUrl"
          name="websiteUrl"
          type="url"
          value={formData.websiteUrl || ''}
          placeholder="https://johndoe.com"
          onChange={handleChange}
          className="flex h-10 w-full rounded-lg bg-gray-100 p-3"
        />
        <ErrorMessage zodError={errors.websiteUrl} />
      </FormField>

      <FormField>
        <PopoverSelector
          label="Country of Residence"
          name="countryOfResidence"
          value={formData.countryOfResidence}
          options={countryOptions}
          setValue={(value) => handleSelectChange('countryOfResidence', value)}
          placeholder="Select country"
          searchPlaceholder="Search country..."
          emptyMessage="No country found."
          triggerClassName="w-full bg-gray-100"
          className="w-full"
        />
        <ErrorMessage zodError={errors.countryOfResidence} />
      </FormField>

      <div className="grid grid-cols-2 gap-4 w-full">
        <FormField>
          <FormLabel
            label={formData.countryOfResidence && formData.countryOfResidence !== 'US' ? 'State/Province' : 'State'}
            htmlFor="stateOfResidence"
          />
          {formData.countryOfResidence === 'US' ? (
            <Select
              name="stateOfResidence"
              value={formData.stateOfResidence || ''}
              onValueChange={(value) => handleSelectChange('stateOfResidence', value)}
            >
              <SelectTrigger className="w-full bg-gray-100">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {usStates.map((state) => (
                    <SelectItem
                      key={state.code}
                      value={state.code}
                    >
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="stateOfResidence"
              name="stateOfResidence"
              type="text"
              value={formData.stateOfResidence || ''}
              placeholder={`Enter ${formData.countryOfResidence && formData.countryOfResidence !== 'US' ? 'state/province' : 'state'}`}
              onChange={handleChange}
              className="flex h-10 w-full rounded-lg bg-gray-100 p-3"
            />
          )}
          <ErrorMessage zodError={errors.stateOfResidence} />
        </FormField>

        <FormField>
          <FormLabel
            label="City"
            htmlFor="cityOfResidence"
          />
          <Input
            id="cityOfResidence"
            name="cityOfResidence"
            type="text"
            value={formData.cityOfResidence || ''}
            placeholder="Enter city"
            onChange={handleChange}
            className="flex h-10 w-full rounded-lg bg-gray-100 p-3"
          />
          <ErrorMessage zodError={errors.cityOfResidence} />
        </FormField>
      </div>

      {formData.type === ExpertProfileType.VETERAN && (
        <>
          <FormField>
            <FormLabel
              label="Country of Service"
              htmlFor="countryOfService"
            />
            <PopoverSelector
              name="countryOfService"
              value={formData.countryOfService}
              options={serviceCountries}
              setValue={(value) => handleSelectChange('countryOfService', value)}
              placeholder="Select country of service"
              searchPlaceholder="Search country..."
              emptyMessage="No country found."
              className="w-full"
              triggerClassName="w-full"
            />
            <ErrorMessage zodError={errors.countryOfService} />
          </FormField>

          <div className="w-full">
            <div className="flex w-full items-end gap-4">
              <FormField className="grow">
                <FormLabel
                  label="Service"
                  htmlFor="serviceId"
                />
                <Select
                  name="serviceId"
                  value={formData.serviceId || ''}
                  onValueChange={(value) => handleSelectChange('serviceId', value)}
                  disabled={!formData.countryOfService || serviceOptions.length === 0}
                >
                  <SelectTrigger className="w-full grow">
                    <SelectValue
                      placeholder={
                        formData.countryOfService ? 'Select service branch' : 'Select Country of Service first'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {isLoadingServices && <SelectItem value="loading">Loading...</SelectItem>}
                      {serviceOptions.map((service) => (
                        <SelectItem
                          key={service.id}
                          value={service.id}
                        >
                          {service.name}
                        </SelectItem>
                      ))}
                      {!isLoadingServices && serviceOptions.length === 0 && formData.countryOfService && (
                        <SelectItem
                          value="no-options"
                          disabled
                        >
                          No services available for this country
                        </SelectItem>
                      )}
                      {!isLoadingServices && !formData.countryOfService && (
                        <SelectItem
                          value="select-country"
                          disabled
                        >
                          Please select a country of service first
                        </SelectItem>
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <ErrorMessage zodError={errors.serviceId} />
              </FormField>
              <Avatar
                altText={selectedService?.name || selectedService?.slug || 'Service Logo'}
                key={formData.serviceId || 'service-logo'}
                imageUrl={
                  formData.serviceId && selectedService?.slug
                    ? `/images/services/${selectedService.slug}.png`
                    : undefined
                }
                initialText={selectedService?.name}
                size="lg"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
